import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Job } from '../../job.model';
import { JobArticleComponent } from '../job-article/job-article.component';
import { UtilsService } from '../../../../../core/utils/utils.service';
import { Article } from '../../../../../service-groups/articles/article.model';
import { Popover } from '@ionic/core/dist/types/components/popover/popover';
import { User } from '../../../../../core/shared-models/user.model';
import { SettingsService } from '../../../../../settings/settings.service';
import { NotificationService } from '../../../../../core/notification/notification.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-job-extension',
    templateUrl: './job-extension.component.html',
    styleUrls: ['./job-extension.component.scss'],
})
export class JobExtensionComponent {
    @Input() jobExtensionFormGroup;
    @Input() job: Job;
    @Input() serviceGroupPhotoCategoriesCount = 0;
    @Input() viewMode: 'all' | 'photos-only' = 'all'; // all if we want to show all infos (default) or photos-only if the component is used on phones in the job-photos segment to only display photos
    @Input() possibleWorkers: Array<User> = [];
    @Input() changesAllowed = false;
    @Input() managementChangesAllowed = false;
    @Input() extensionExpanded = true;
    // eslint-disable-next-line
    @Input('enableDuplication') duplicationAllowed = true;
    @Input() deletionAllowed = true;
    @Input() asDialog = false;
    @Input() reorderActive = false;

    @Output() duplicateJobExtensionRequested = new EventEmitter<any>();
    @Output() deleteJobExtensionRequested = new EventEmitter<any>();
    @Output() refreshJobExtensionFieldsRequested = new EventEmitter<any>();
    @Output() jobExtensionFormGroupChanged = new EventEmitter();
    @Output() jobSaveRequested = new EventEmitter();
    @Output() lockRequested = new EventEmitter<string>();
    @Output() articleHelpModalClicked = new EventEmitter<Article>();
    @Output() removedJobLinkId = new EventEmitter<string>();
    @Output() addedJobLinkId = new EventEmitter<string>();

    @ViewChild('articleComponent') articleComponent: JobArticleComponent;
    @ViewChild('jobExtensionWorkerPopover') jobExtensionWorkerPopover: Popover;
    @ViewChild('jobExtensionDatePopover') jobExtensionDatePopover: Popover;

    constructor(
        public utils: UtilsService,
        public settingsSrv: SettingsService,
        private notify: NotificationService,
        private translate: TranslateService
    ) {}

    /**
     * Triggered when the user wants to duplicate the current job extension. Emits an event because the parent component handles it
     */
    onDuplicateJobExtension(event?: any) {
        if (event != null) {
            event.stopPropagation();
        }
        this.duplicateJobExtensionRequested.emit();
    }

    /**
     * Triggered when the user wants to delete the current job extension. Emits an event because the parent component handles it
     */
    onDeleteJobExtension(event?: any) {
        if (event != null) {
            event.stopPropagation();
        }
        this.deleteJobExtensionRequested.emit();
    }

    /**
     * Triggered when the user wants to refresh the current job extension fields. Emits an event because the parent component handles it
     */
    onRefreshJobExtensionFields() {
        this.refreshJobExtensionFieldsRequested.emit();
    }

    /**
     * Can be called to trigger field visibility calculation of article component child
     */
    triggerFieldVisibilityCalculation() {
        this.articleComponent.calculateFieldVisibility();
    }

    /**
     * Pseudo function
     */
    actionsButton(event: any) {
        // prevent button click from closing accordion
        event.stopPropagation();
    }

    /**
     * Triggered when the user clicks on the info icon
     */
    onOpenArticleHelp(event?: any) {
        if (event != null) {
            event.stopPropagation();
        }
        this.articleHelpModalClicked.emit(this.jobExtensionFormGroup?.get('article')?.value);
    }

    /**
     * Triggered when the user clicks the job extension worker to change it
     */
    onChangeJobExtensionWorker(clickEvent?: any) {
        clickEvent.stopPropagation();
        this.jobExtensionWorkerPopover.present(clickEvent);
    }

    /**
     * Triggered when the user clicks the job extension date to change it
     */
    onChangeJobExtensionDate(clickEvent?: any) {
        clickEvent.stopPropagation();
        this.jobExtensionDatePopover.present(clickEvent);
    }

    /**
     * Sets date accordingly after change in sub-component
     * @param changeEvent
     */
    onJobExtensionDateChanged(changeEvent: { timestamp: number; finalChange: boolean }) {
        this.jobExtensionFormGroup.get('date').setValue(changeEvent.timestamp);
        if (changeEvent.finalChange) {
            this.jobExtensionDatePopover.dismiss();
        }
    }

    async onManuallyLockJobExtension() {
        if (!this.changesAllowed) return;
        if (this.jobExtensionFormGroup.get('locked').value) {
            if (this.settingsSrv.currentUserRights.canUnlockJobs) {
                this.jobExtensionFormGroup.get('locked').setValue(false);
            }
        } else {
            if (this.settingsSrv.currentUserRights.canLockJobs) {
                if (!this.settingsSrv.currentUserRights.canUnlockJobs) {
                    try {
                        await this.notify.confirm(
                            this.translate.instant('projects.jobs.details.confirm-lock-no-unlock-right'),
                            this.translate.instant('projects.jobs.details.confirm-lock-title'),
                            this.translate.instant('projects.jobs.details.confirm-lock-btn')
                        );
                    } catch {
                        return;
                    }
                }
                this.jobExtensionFormGroup.get('locked').setValue(true);
            }
        }
    }
}
